/**
 * AES-256-GCM payload encryption shared with the backend (Web Crypto API).
 *
 * Wire format (base64):  iv(12 bytes) || ciphertext || authTag(16 bytes)
 * Web Crypto's AES-GCM appends the 16-byte tag to the ciphertext, which is
 * exactly what the backend (Node crypto) expects/produces — so the same
 * envelope round-trips both ways.
 */

const KEY_HEX = import.meta.env.VITE_API_AES_KEY || '';
export const APP_TOKEN = import.meta.env.VITE_APP_TOKEN || '';

const IV_LEN = 12;

// Feature is enabled only with a valid 32-byte (64 hex chars) key.
export const cryptoEnabled = () => KEY_HEX.length === 64;

const hexToBytes = (hex) => {
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < bytes.length; i++) {
    bytes[i] = parseInt(hex.substr(i * 2, 2), 16);
  }
  return bytes;
};

const bytesToBase64 = (bytes) => {
  const arr = new Uint8Array(bytes);
  let binary = '';
  for (let i = 0; i < arr.length; i++) binary += String.fromCharCode(arr[i]);
  return btoa(binary);
};

const base64ToBytes = (b64) => {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
};

// Import the raw key once and reuse it.
let keyPromise = null;
const getKey = () => {
  if (!keyPromise) {
    keyPromise = crypto.subtle.importKey(
      'raw',
      hexToBytes(KEY_HEX),
      { name: 'AES-GCM' },
      false,
      ['encrypt', 'decrypt']
    );
  }
  return keyPromise;
};

/** Encrypt a JS value into the base64 envelope string. */
export const encryptPayload = async (value) => {
  const key = await getKey();
  const iv = crypto.getRandomValues(new Uint8Array(IV_LEN));
  const data = new TextEncoder().encode(JSON.stringify(value));
  const ctTag = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv, tagLength: 128 },
    key,
    data
  );
  const ctTagBytes = new Uint8Array(ctTag);
  const out = new Uint8Array(IV_LEN + ctTagBytes.length);
  out.set(iv, 0);
  out.set(ctTagBytes, IV_LEN);
  return bytesToBase64(out);
};

/** Decrypt the base64 envelope string back into a JS value. */
export const decryptPayload = async (b64) => {
  const key = await getKey();
  const buf = base64ToBytes(b64);
  const iv = buf.subarray(0, IV_LEN);
  const ctTag = buf.subarray(IV_LEN);
  const plain = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv, tagLength: 128 },
    key,
    ctTag
  );
  return JSON.parse(new TextDecoder().decode(plain));
};

export default { cryptoEnabled, encryptPayload, decryptPayload, APP_TOKEN };
